import { animate, state, style, transition, trigger } from "@angular/animations";
import { Component, HostBinding, Input, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { MenuItem } from "primeng/api";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { MenuChangeEvent } from "src/app/layout/api/menuchangeevent";
import { LayoutService } from "src/app/layout/service/app.layout.service";

@Component({
  selector: "app-menuitem",
  templateUrl: "./app.menuitem.component.html",
  animations: [
    trigger("children", [
      state(
        "collapsed",
        style({
          height: "0",
        }),
      ),
      state(
        "expanded",
        style({
          height: "*",
        }),
      ),
      transition("collapsed <=> expanded", animate("400ms cubic-bezier(0.86, 0, 0.07, 1)")),
    ]),
  ],
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
  private readonly destroySubject = new Subject<void>();

  @Input({ required: true }) item!: MenuItem;

  @Input() index!: number;

  @Input() @HostBinding("class.layout-root-menuitem") root!: boolean;

  @Input() parentKey!: string;

  active = false;

  key = "";

  constructor(
    public layoutService: LayoutService,
    public router: Router,
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        if (this.item.routerLink) {
          this.updateActiveStateFromRoute();
        }
      });
  }

  ngOnInit(): void {
    this.key = this.parentKey ? `${this.parentKey}-${this.index}` : String(this.index);
    if (this.item.routerLink) {
      this.updateActiveStateFromRoute();
    }
  }

  updateActiveStateFromRoute(): void {
    const activeRoute = this.router.isActive(this.item.routerLink[0], {
      paths: "exact",
      queryParams: "ignored",
      matrixParams: "ignored",
      fragment: "ignored",
    });

    if (activeRoute) {
      this.onMenuStateChange({ key: this.key, routeEvent: true });
    }
  }

  onMenuStateChange(value: MenuChangeEvent): void {
    if (value.routeEvent) {
      this.active = Boolean(value.key === this.key || value.key.startsWith(`${this.key}-`));
    } else if (value.key !== this.key && !value.key.startsWith(`${this.key}-`)) {
      this.active = false;
    }
  }

  itemClick(event: Event): void {
    // avoid processing disabled items
    if (this.item.disabled) {
      event.preventDefault();
      return;
    }

    // execute command
    if (this.item.command) {
      this.item.command({ originalEvent: event, item: this.item });
    }

    // toggle active state
    if (this.item.items) {
      this.active = !this.active;
    }

    this.onMenuStateChange({ key: this.key });
  }

  get submenuAnimation(): string {
    if (this.root) {
      return "expanded";
    }
    return this.active ? "expanded" : "collapsed";
  }

  @HostBinding("class.active-menuitem")
  get activeClass(): boolean {
    return this.active && !this.root;
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
